import React, { useState, useEffect } from 'react';
import { itineraryService } from '../services/itinerary';

interface Props {
  itinerary: any;
  onClose: () => void;
  onSuccess: () => void;
}

const SetPriceModal: React.FC<Props> = ({ itinerary, onClose, onSuccess }) => {
  const totalPax = (itinerary.basic_info?.no_of_pax || 0) + (itinerary.basic_info?.no_of_children || 0);
  const priceType = totalPax >= 15 ? 'Group' : 'FIT';
  const currency = itinerary.itinerary_currency || 'IDR';
  const systemPrice = itinerary.system_price?.[priceType]?.[currency]?.total || 0;
  
  // Use stored cost_summary from database (same as Streamlit approach)
  const costSummary = itinerary.cost_summary || {};
  const baseCost = calculateBaseCostFromSummary(costSummary);
  
  function calculateBaseCostFromSummary(summary: any): number {
    if (!summary.accommodations && !summary.day_wise_details) {
      // Fallback to manual calculation if cost_summary not available
      const totalRoomCost = itinerary.accommodations?.reduce((sum: number, acc: any) => sum + (acc.total_room_cost || 0), 0) || 0;
      const totalSurcharge = itinerary.accommodations?.reduce((sum: number, acc: any) => sum + (acc.total_surcharge || 0), 0) || 0;
      const totalExtraBedCost = itinerary.accommodations?.reduce((sum: number, acc: any) => sum + (acc.total_extra_bed_cost || 0), 0) || 0;
      const totalKidsBreakfastCost = itinerary.accommodations?.reduce((sum: number, acc: any) => sum + (acc.total_kids_breakfast_cost || 0), 0) || 0;
      const totalActivitiesCost = itinerary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.activities_cost || 0), 0) || 0;
      const totalMealsCost = itinerary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.meals_cost || 0), 0) || 0;
      const totalVehicleCost = itinerary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.vehicle_cost || 0), 0) || 0;
      const totalAdditionalActivityCost = itinerary.additional_activities?.reduce((sum: number, act: any) => sum + (act.total_activity_cost || 0), 0) || 0;
      const totalAddonsCost = itinerary.extra_expenses?.reduce((sum: number, exp: any) => sum + (exp.cost || 0), 0) || 0;
      const totalDayAddonsCost = itinerary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.day_addons_cost || 0), 0) || 0;
      const totalDayExtrasCost = itinerary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.day_extras_cost || 0), 0) || 0;
      // Include surcharge in total calculation
      const totalAccommodationCost = totalRoomCost + totalSurcharge + totalExtraBedCost + totalKidsBreakfastCost;
      const totalAllActivities = totalActivitiesCost + totalAdditionalActivityCost + totalDayAddonsCost + totalDayExtrasCost;
      return totalAccommodationCost + totalAllActivities + totalMealsCost + totalVehicleCost + totalAddonsCost;
    }
    
    // Use stored cost_summary data
    const totalRoomCost = summary.accommodations?.reduce((sum: number, acc: any) => sum + (acc.total_room_cost || 0), 0) || 0;
    const totalSurcharge = summary.accommodations?.reduce((sum: number, acc: any) => sum + (acc.total_surcharge || 0), 0) || 0;
    const totalExtraBedCost = summary.accommodations?.reduce((sum: number, acc: any) => sum + (acc.total_extra_bed_cost || 0), 0) || 0;
    const totalKidsBreakfastCost = summary.accommodations?.reduce((sum: number, acc: any) => sum + (acc.total_kids_breakfast_cost || 0), 0) || 0;
    const totalActivitiesCost = summary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.activities_cost || 0), 0) || 0;
    const totalMealsCost = summary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.meals_cost || 0), 0) || 0;
    const totalVehicleCost = summary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.vehicle_cost || 0), 0) || 0;
    const totalAdditionalActivityCost = summary.additional_activities?.reduce((sum: number, act: any) => sum + (act.total_activity_cost || 0), 0) || 0;
    const totalAddonsCost = summary.extra_expenses?.reduce((sum: number, exp: any) => sum + (exp.cost || 0), 0) || 0;
    const totalDayAddonsCost = summary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.day_addons_cost || 0), 0) || 0;
    const totalDayExtrasCost = summary.day_wise_details?.reduce((sum: number, day: any) => sum + (day.day_extras_cost || 0), 0) || 0;
    // Include surcharge in total calculation
    const totalAccommodationCost = totalRoomCost + totalSurcharge + totalExtraBedCost + totalKidsBreakfastCost;
    const totalAllActivities = totalActivitiesCost + totalAdditionalActivityCost + totalDayAddonsCost + totalDayExtrasCost;
    return totalAccommodationCost + totalAllActivities + totalMealsCost + totalVehicleCost + totalAddonsCost;
  }
  
  // Backend controls what data is sent - only admins get additional
  const hasAdminData = itinerary.additional !== undefined;
  const additionalFromBackend = itinerary.additional || 0;
  
  // Extract given price - handle both old and new structure
  const currentGivenPrice = itinerary.given_price?.total || itinerary.given_price?.[currency]?.total || 0;
  const [givenPrice, setGivenPrice] = useState(currentGivenPrice);
  const [additional, setAdditional] = useState(additionalFromBackend);
  
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  
  console.log('SetPriceModal - Full itinerary:', itinerary);
  console.log('SetPriceModal - base_cost:', itinerary.base_cost);
  console.log('SetPriceModal - additional:', itinerary.additional);
  console.log('SetPriceModal - system_price:', itinerary.system_price);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [notification, setNotification] = useState<{type: 'success'|'error'|'warning', message: string} | null>(null);

  const handleSave = async () => {
    // Show confirmation if price is below recommended
    if (givenPrice < systemPrice) {
      setShowConfirm(true);
      return;
    }
    await savePrice();
  };

  const savePrice = async () => {
    try {
      setSaving(true);
      setShowConfirm(false);
      
      const itineraryId = itinerary.id || itinerary.itinerary_id;
      console.log('Saving price for itinerary:', itineraryId, 'Price:', givenPrice);
      
      const updatedItinerary = {
        ...itinerary,
        given_price: {
          currency: currency,
          total: givenPrice,
          categories: {}
        }
      };
      const response = await itineraryService.updateItinerary(itineraryId, updatedItinerary);
      console.log('Save response:', response);
      
      if (response.warning) {
        setNotification({type: 'warning', message: response.warning});
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setNotification({type: 'success', message: 'Price saved successfully'});
        setTimeout(() => {
          onSuccess();
        }, 1500);
      }
    } catch (error: any) {
      console.error('Error saving price:', error);
      console.error('Error details:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Error saving price';
      setNotification({type: 'error', message: errorMsg});
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-white px-4 py-3">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold">💰 Set Price</h2>
              <p className="text-white text-opacity-90 text-sm">
                <span className="font-mono font-bold">{itinerary.wyn_itinerary_id || `WH_${itinerary.id || itinerary.itinerary_id}`}</span>
                <span className="mx-2">•</span>
                <span className="font-medium">{itinerary.basic_info?.itinerary_name || 'Untitled'}</span>
              </p>
            </div>
            <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1.5">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {hasAdminData ? (
            <>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Total Cost:</span>
                  <span className="font-semibold">{currency} {baseCost.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Additional:</span>
                  <input
                    type="number"
                    value={additional}
                    onChange={(e) => setAdditional(parseFloat(e.target.value) || 0)}
                    className="w-32 px-2 py-1 border border-gray-300 rounded text-right"
                  />
                </div>
                <div className="flex justify-between text-sm pt-2 border-t">
                  <span className="text-gray-700 font-medium">Subtotal:</span>
                  <span className="font-semibold">{currency} {(baseCost + additional).toLocaleString()}</span>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">{priceType} - System Recommended:</span>
                  <span className="font-bold text-lg">{currency} {systemPrice.toLocaleString()}</span>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between">
                <span className="text-gray-700 font-medium">{priceType} - Recommended Price:</span>
                <span className="font-bold text-lg">{currency} {systemPrice.toLocaleString()}</span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Given Price ({currency})
            </label>
            <input
              type="number"
              value={givenPrice || ''}
              onChange={(e) => setGivenPrice(parseFloat(e.target.value) || 0)}
              className="input-field"
              placeholder="Enter given price"
            />
            {currentGivenPrice > 0 && (
              <p className="text-xs text-gray-500 mt-1">Current: {currency} {currentGivenPrice.toLocaleString()}</p>
            )}

          </div>
        </div>

        <div className="border-t border-gray-200 p-6 bg-gray-50 flex gap-4">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary flex-1">
            {saving ? 'Saving...' : 'Save Price'}
          </button>
        </div>
      </div>
    </div>

    {/* Confirmation Dialog */}
    {showConfirm && (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[10001] p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Price Below Recommended</h3>
              <p className="text-sm text-gray-600 mb-4">
                You are setting a price <strong>{currency} {givenPrice.toLocaleString()}</strong> below the recommended price <strong>{currency} {systemPrice.toLocaleString()}</strong>. Are you sure you want to continue?
              </p>
              <div className="flex gap-3">
                <button onClick={() => setShowConfirm(false)} className="btn-secondary flex-1">Cancel</button>
                <button onClick={savePrice} disabled={saving} className="btn-primary flex-1">
                  {saving ? 'Saving...' : 'Yes, Continue'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Notification Toast */}
    {notification && (
      <div className="fixed top-4 right-4 z-[70] animate-slide-in">
        <div className={`rounded-lg shadow-lg p-4 max-w-md ${
          notification.type === 'success' ? 'bg-green-50 border border-green-200' :
          notification.type === 'error' ? 'bg-red-50 border border-red-200' :
          'bg-yellow-50 border border-yellow-200'
        }`}>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              {notification.type === 'success' && (
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {notification.type === 'error' && (
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {notification.type === 'warning' && (
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
            </div>
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                notification.type === 'success' ? 'text-green-800' :
                notification.type === 'error' ? 'text-red-800' :
                'text-yellow-800'
              }`}>
                {notification.message}
              </p>
            </div>
            <button onClick={() => setNotification(null)} className="flex-shrink-0 text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default SetPriceModal;
